import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { useAuth } from "../../context/AuthContext";
import { useConsents } from "../../hooks/useConsents";
import { CONSENT_ITEMS } from "./consentItems";
import { ConsentRow } from "./ConsentRow";
import { LEGAL_VERSION } from "./content";

// بوابة موافقة صريحة إلزامية: لا يُستخدم التطبيق قبل تأشير كل مستند على حدة (آبل 5.1.1 / Play User Data).
// توسيط عمودي وأفقي على كل الشاشات، مع مراعاة safe-area للموبايل.
export const ConsentGate = ({ children }) => {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const { state, loading, accept } = useConsents();
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);

  // أثناء التحقق من حالة الموافقات: نعرض شاشة تحميل — لا نُظهر children أبداً قبل الحسم
  // (يمنع ومضة المحتوى قبل الموافقة الصريحة — امتثال Apple 5.1.1 / GDPR Art.7).
  if (loading)
    return (
      <div
        data-testid="consent-gate-loading"
        className="flex min-h-[100dvh] items-center justify-center bg-background"
      >
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  if (!state?.blocked) return children;
  const missing = state.missing;
  const allChecked = missing.every((k) => checked[k]);

  const submit = async () => {
    setBusy(true);
    try {
      await accept(missing);
      toast.success(t("consent.saved"));
    } catch { toast.error(t("errors.generic")); }
    finally { setBusy(false); }
  };

  return (
    <div
      data-testid="consent-gate"
      className="flex min-h-[100dvh] items-center justify-center bg-background px-4 py-8 pt-[max(2rem,calc(env(safe-area-inset-top)+1rem))] pb-[max(2rem,calc(env(safe-area-inset-bottom)+1rem))]"
    >
      <div className="w-full max-w-xl">
        <NeuCard className="animate-rise-in p-6 sm:p-10">
          <div className="flex items-center gap-3">
            <div className="neu flex h-14 w-14 items-center justify-center rounded-2xl p-0">
              <ShieldCheck className="h-7 w-7 text-primary" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl leading-tight font-black tracking-tight sm:text-3xl">{t("consent.title")}</h1>
              <p className="mt-1 text-xs font-semibold text-muted-foreground">
                {t("legal.version")} {LEGAL_VERSION}
              </p>
            </div>
          </div>
          <p className="mt-5 text-base leading-7 text-muted-foreground">{t("consent.intro")}</p>
          <div className="mt-6 grid gap-3">
            {CONSENT_ITEMS.filter((i) => missing.includes(i.id)).map((item) => (
              <ConsentRow
                key={item.id}
                id={item.id}
                checked={Boolean(checked[item.id])}
                onChange={(v) => setChecked({ ...checked, [item.id]: v })}
                label={t(item.label)}
                hint={t(item.hint)}
                to={item.to}
                linkLabel={t(item.link)}
              />
            ))}
          </div>
          <NeuButton
            variant="primary"
            className="mt-7 w-full py-5 text-base font-bold sm:text-lg"
            data-testid="consent-gate-submit"
            disabled={!allChecked || busy}
            onClick={submit}
          >
            {busy ? t("app.loading") : t("consent.agreeAndContinue")}
          </NeuButton>
          <NeuButton
            className="mx-auto mt-3 block px-5 py-2 text-sm text-destructive"
            data-testid="consent-gate-decline"
            onClick={signOut}
          >
            {t("consent.declineAndExit")}
          </NeuButton>
        </NeuCard>
      </div>
    </div>
  );
};
